<script lang="ts" setup>
import VueSlider from 'vue-3-slider-component';
import { debounce } from '@antfu/utils';
import type { RangeFilter } from '~/types/Catalog';
import {
  canUseRangeSlider,
  getRangeFilterLimits,
  getSafeRangeFilterValues,
  validateRangeFilterDraft,
  type RangeFilterDraft,
  type RangeFilterValues,
} from '~/utils/rangeFilter';

const { filter } = defineProps<{ filter: RangeFilter }>();

const route = useRoute();
const router = useRouter();
const limits = computed(() => getRangeFilterLimits(filter.details.limit_min, filter.details.limit_max));
const range = ref<RangeFilterValues>([0, 0]);
const draft = ref<RangeFilterDraft>(['', '']);
const errors = ref<RangeFilterDraft>(['', '']);
let isEditing = false;
let submittedPath: string | null = null;

const debouncedApply = debounce(700, applyDraft);

function syncFromFilter() {
  range.value = limits.value ? getSafeRangeFilterValues(filter.details.values, limits.value) : [0, 0];

  // A response to the previous request must not erase text typed while it was loading.
  if (!isEditing) {
    draft.value = [String(range.value[0]), String(range.value[1])];
    errors.value = ['', ''];
  }
}

watch(() => [filter.slug, filter.details.values, filter.details.limit_min, filter.details.limit_max], syncFromFilter, {
  immediate: true,
  deep: true,
});

watch(
  () => route.fullPath,
  () => {
    if (route.fullPath === submittedPath) {
      submittedPath = null;
      return;
    }

    // Reset, back/forward and other navigation cancel pending edits.
    debouncedApply.cancel({ upcomingOnly: true });
    submittedPath = null;
    isEditing = false;
    syncFromFilter();
  },
);

onBeforeUnmount(() => debouncedApply.cancel());

function handleInput(index: 0 | 1, value: string) {
  isEditing = true;
  draft.value[index] = value;
  errors.value = ['', ''];
  debouncedApply();
}

function handleSliderChange(values: unknown) {
  if (!limits.value) return;

  debouncedApply.cancel({ upcomingOnly: true });
  range.value = getSafeRangeFilterValues(values, limits.value);
  draft.value = [String(range.value[0]), String(range.value[1])];
  errors.value = ['', ''];
  isEditing = true;
  debouncedApply();
}

async function applyDraft() {
  debouncedApply.cancel({ upcomingOnly: true });
  const result = validateRangeFilterDraft(draft.value, limits.value);
  errors.value = result.errors;

  if (!result.values) return;

  range.value = result.values;
  isEditing = false;

  const filterSlug = `filter[${filter.slug}]`;
  const query = { ...route.query };

  if (result.queryValue === undefined) delete query[filterSlug];
  else query[filterSlug] = result.queryValue;

  // Do not reset pagination or reload the catalog when the range has not changed.
  if (query[filterSlug] === route.query[filterSlug]) return;

  delete query.page;
  submittedPath = router.resolve({ path: route.path, query }).fullPath;

  await navigateTo({ path: route.path, query });
}
</script>

<template>
  <div class="range-filter">
    <div class="flex justify-between gap-2">
      <InputField
        :model-value="draft[0]"
        :placeholder="String(filter.details.limit_min)"
        :error="errors[0]"
        :disabled="!limits"
        class="flex-1 min-w-0"
        name="from"
        @update:model-value="handleInput(0, $event)"
        @submit="applyDraft"
      />

      <InputField
        :model-value="draft[1]"
        :placeholder="String(filter.details.limit_max)"
        :error="errors[1]"
        :disabled="!limits"
        class="flex-1 min-w-0"
        name="to"
        @update:model-value="handleInput(1, $event)"
        @submit="applyDraft"
      />
    </div>

    <div
      v-if="canUseRangeSlider(limits, filter.step)"
      class="px-2"
    >
      <VueSlider
        :model-value="range"
        :dot-options="{ tooltip: 'none', focusStyle: { boxShadow: '.5px .5px 3px 2px #00000042' } }"
        :dot-size="16"
        :interval="filter.step"
        :max="filter.details.limit_max"
        :min="filter.details.limit_min"
        :process-style="{ backgroundColor: '#393d46' }"
        :rail-style="{ backgroundColor: '#d7d8da' }"
        @update:model-value="handleSliderChange"
      />
    </div>
  </div>
</template>

<style lang="scss">
.range-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: $text-sm;
}
</style>
